import { useState, useEffect } from 'react';
import { FormField, Input } from '../../../components/ui/Form';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { CURRENCIES, CurrencyCode } from '../../../config/currency';
import { useSettings } from '../../../hooks/useSettings';
import { useCurrencyStore } from '../../../stores/currencyStore';
import { RefreshCw, Save, AlertTriangle, CheckCircle } from 'lucide-react';

interface ExchangeRateInput {
  code: CurrencyCode;
  rate: string;
  isValid: boolean;
}

/**
 * Composant dédié à la gestion des taux de change
 * Permet de configurer tous les taux: 1 [devise] = X CDF
 */
export function ExchangeRatesSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const { setRatesFromRecord } = useCurrencyStore();
  
  const [rates, setRates] = useState<ExchangeRateInput[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // Initialiser les taux depuis les settings
  useEffect(() => {
    if (settings?.general?.exchangeRates) {
      const existingRates = settings.general.exchangeRates;
      
      const rateInputs: ExchangeRateInput[] = Object.keys(CURRENCIES)
        .filter(code => code !== 'CDF') // Exclure la devise de base
        .map(code => ({
          code: code as CurrencyCode,
          rate: existingRates[code]?.toString() || '',
          isValid: !existingRates[code] || existingRates[code] > 0
        }));
      
      setRates(rateInputs);
    }
  }, [settings]);
  
  const handleRateChange = (code: CurrencyCode, value: string) => {
    setRates(prev => prev.map(r => {
      if (r.code === code) {
        const numValue = parseFloat(value);
        return {
          ...r,
          rate: value,
          isValid: value === '' || (!isNaN(numValue) && numValue > 0)
        };
      }
      return r;
    }));
    setSaveStatus('idle');
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    
    try {
      // Construire l'objet des taux
      const exchangeRates: Record<string, number> = { CDF: 1 };
      
      rates.forEach(r => {
        if (r.rate && r.isValid) {
          const rate = parseFloat(r.rate);
          if (!isNaN(rate) && rate > 0) {
            exchangeRates[r.code] = rate;
          }
        }
      });
      
      // Sauvegarder via l'API settings
      await updateSettings({
        category: 'general',
        settings: {
          ...settings?.general,
          exchangeRates
        }
      });
      
      // Mettre à jour le store local
      setRatesFromRecord(exchangeRates);
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des taux:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };
  
  const hasInvalidRates = rates.some(r => !r.isValid);
  const hasChanges = rates.some(r => {
    const originalRate = settings?.general?.exchangeRates?.[r.code];
    return r.rate !== (originalRate?.toString() || '');
  });
  
  if (loading) {
    return (
      <Card title="Taux de change">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Chargement des taux...</span>
        </div>
      </Card>
    );
  }
  
  return (
    <Card title="Taux de change">
      <div className="space-y-6">
        {/* Sous-titre */}
        <p className="text-sm text-gray-600 dark:text-gray-400 -mt-2">
          Configurez les taux de conversion vers le Franc Congolais (CDF)
        </p>
        
        {/* Information sur la devise de base */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Devise de base:</strong> {CURRENCIES.CDF.name} ({CURRENCIES.CDF.symbol})
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Tous les montants sont stockés en CDF. Les taux ci-dessous définissent combien de CDF vaut 1 unité de chaque devise.
          </p>
        </div>
        
        {/* Grille des taux */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rates.map(r => {
            const currency = CURRENCIES[r.code];
            if (!currency) return null;
            
            return (
              <div key={r.code} className="space-y-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{currency.symbol}</span>
                  <span className="text-gray-500 text-sm">{currency.name}</span>
                </div>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      1 {currency.symbol} =
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={r.rate}
                      onChange={(e) => handleRateChange(r.code, e.target.value)}
                      className={`pl-20 ${!r.isValid ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      CDF
                    </span>
                  </div>
                {!r.isValid && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Le taux doit être supérieur à 0
                  </p>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Taux actuels configurés */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Taux actuellement configurés
          </h4>
          <div className="flex flex-wrap gap-2">
            {rates.filter(r => r.rate && r.isValid).map(r => {
              const currency = CURRENCIES[r.code];
              return (
                <span 
                  key={r.code}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                >
                  1 {currency.symbol} = {parseFloat(r.rate).toLocaleString()} CDF
                </span>
              );
            })}
            {rates.filter(r => r.rate && r.isValid).length === 0 && (
              <span className="text-sm text-gray-500">
                Aucun taux configuré. Veuillez saisir les taux de change.
              </span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            {saveStatus === 'success' && (
              <span className="flex items-center text-green-600 text-sm">
                <CheckCircle className="h-4 w-4 mr-1" />
                Taux enregistrés avec succès
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center text-red-600 text-sm">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Erreur lors de l'enregistrement
              </span>
            )}
          </div>
          
          <Button
            onClick={handleSave}
            disabled={isSaving || hasInvalidRates || !hasChanges}
            icon={isSaving ? RefreshCw : Save}
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer les taux'}
          </Button>
        </div>
        
        {/* Aide */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm text-gray-600 dark:text-gray-400">
          <h5 className="font-medium mb-2">Comment utiliser les taux de change ?</h5>
          <ul className="list-disc list-inside space-y-1">
            <li>Lors de la saisie d'une écriture en devise étrangère, le montant sera automatiquement converti en CDF</li>
            <li>Les rapports financiers sont toujours générés en CDF (devise de base)</li>
            <li>La devise d'affichage dans le header permet de visualiser les montants dans une autre devise</li>
            <li>Mettez à jour régulièrement les taux pour refléter les valeurs actuelles du marché</li>
          </ul>
        </div>
      </div>
    </Card>
  );
}
